import React, { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "../../components/ui/button";
import { DifficultySelector, GameOverScreen } from "./GameShell";
import { RotateCcw } from "lucide-react";

const DIFFICULTY_CONFIG = {
  easy:   { baseSpeed: 3.6, speedIncrease: 0.3,  roadLength: 400, rounds: 5, brakeForce: 10 },
  medium: { baseSpeed: 5.6, speedIncrease: 0.5,  roadLength: 340, rounds: 5, brakeForce: 8 },
  hard:   { baseSpeed: 7.6, speedIncrease: 0.8,  roadLength: 280, rounds: 5, brakeForce: 6 },
};

const TUTORIAL_STEPS = [
  { visual: "🚗", title: "The Car", description: "A car speeds down the road toward a rabbit. Your job is to stop it!" },
  { visual: "🐇", title: "Save the Rabbit", description: "Tap or press SPACE to brake before the car reaches the rabbit." },
  { visual: "⚡", title: "React Fast", description: "The closer you stop to the rabbit without hitting it, the more points you earn. But too late and it's game over!" },
  { visual: "📈", title: "Increasing Speed", description: "Each round the car gets faster. Higher difficulties mean faster cars and less room to stop." },
];

const CAR_EMOJI = "🚗";
const RABBIT_EMOJI = "🐇";

const ReactionTestGame = ({ onComplete, onBack, t }) => {
  const [difficulty, setDifficulty] = useState(null);
  const [gameState, setGameState] = useState("idle"); // idle | countdown | driving | braking | stopped | crashed | roundEnd | finished
  const [round, setRound] = useState(1);
  const [score, setScore] = useState(0);
  const [carPos, setCarPos] = useState(0);
  const [rabbitPos, setRabbitPos] = useState(0);
  const [countdown, setCountdown] = useState(3);
  const [roundScores, setRoundScores] = useState([]);
  const [crashed, setCrashed] = useState(false);
  const animRef = useRef(null);
  const carSpeed = useRef(0);
  const configRef = useRef(null);
  const roadRef = useRef(null);

  const config = configRef.current;

  const startCountdown = useCallback(() => {
    setGameState("countdown");
    setCountdown(3);
    setCarPos(0);
  }, []);

  const startGame = useCallback((diff) => {
    setDifficulty(diff);
    configRef.current = DIFFICULTY_CONFIG[diff];
    setRound(1);
    setScore(0);
    setRoundScores([]);
    setCrashed(false);
    startCountdown();
  }, [startCountdown]);

  useEffect(() => {
    if (gameState !== "countdown") return;
    if (countdown <= 0) {
      setGameState("driving");
      return;
    }
    const timer = setTimeout(() => setCountdown((c) => c - 1), 700);
    return () => clearTimeout(timer);
  }, [gameState, countdown]);

  // Driving animation
  useEffect(() => {
    if (gameState !== "driving" || !configRef.current) return;
    const cfg = configRef.current;
    const speed = cfg.baseSpeed + (round - 1) * cfg.speedIncrease;
    carSpeed.current = speed;
    const rabbitAt = cfg.roadLength;
    setRabbitPos(rabbitAt);

    let pos = 0;
    const drive = () => {
      pos += carSpeed.current;
      if (pos >= rabbitAt) {
        // Crashed into rabbit
        setCarPos(rabbitAt);
        setCrashed(true);
        setGameState("crashed");
        return;
      }
      setCarPos(pos);
      animRef.current = requestAnimationFrame(drive);
    };
    animRef.current = requestAnimationFrame(drive);
    return () => cancelAnimationFrame(animRef.current);
  }, [gameState, round]);

  const handleBrake = useCallback(() => {
    if (gameState !== "driving") return;
    cancelAnimationFrame(animRef.current);
    setGameState("braking");
  }, [gameState]);

  // Braking animation - car decelerates. Uses refs to avoid stale closure
  const carPosRef = useRef(0);
  useEffect(() => { carPosRef.current = carPos; }, [carPos]);

  useEffect(() => {
    if (gameState !== "braking" || !configRef.current) return;
    const cfg = configRef.current;
    let speed = carSpeed.current;
    let pos = carPosRef.current;
    const rabbitAt = cfg.roadLength;

    const brake = () => {
      speed = Math.max(0, speed - cfg.brakeForce * 0.16);
      pos += speed;
      if (pos >= rabbitAt) {
        setCarPos(rabbitAt);
        setCrashed(true);
        setGameState("crashed");
        return;
      }
      setCarPos(pos);
      if (speed <= 0.1) {
        // Stopped successfully
        const distance = rabbitAt - pos;
        const maxPossible = 200;
        const closenessBonus = Math.max(0, Math.round((1 - distance / rabbitAt) * maxPossible));
        const roundScore = closenessBonus;
        setScore((s) => s + roundScore);
        setRoundScores((rs) => [...rs, { round, distance: Math.round(distance), score: roundScore }]);
        setGameState("roundEnd");
        return;
      }
      animRef.current = requestAnimationFrame(brake);
    };
    animRef.current = requestAnimationFrame(brake);
    return () => cancelAnimationFrame(animRef.current);
  }, [gameState, round]);

  // Keyboard support
  useEffect(() => {
    const onKey = (e) => {
      if (e.code === "Space" || e.key === " ") {
        e.preventDefault();
        handleBrake();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handleBrake]);

  const nextRound = () => {
    if (round >= configRef.current.rounds) {
      setGameState("finished");
      const avgDist = roundScores.length > 0
        ? Math.round(roundScores.reduce((a, r) => a + r.distance, 0) / roundScores.length)
        : 0;
      onComplete(score, round, avgDist, difficulty);
    } else {
      setRound((r) => r + 1);
      setCarPos(0);
      startCountdown();
    }
  };

  const resetGame = () => {
    setCrashed(false);
    startGame(difficulty);
  };

  // Difficulty selection
  if (!difficulty) {
    return (
      <DifficultySelector
        gameName={t("games.reactionTest.name")}
        gameDescription={t("games.reactionTest.description")}
        tutorialSteps={TUTORIAL_STEPS}
        onStart={startGame}
        onBack={onBack}
        t={t}
      />
    );
  }

  const roadWidth = configRef.current?.roadLength || 400;
  const progress = roadWidth > 0 ? (carPos / roadWidth) * 100 : 0;

  // Finished
  if (gameState === "finished") {
    return (
      <GameOverScreen
        score={score}
        subtitle={`${roundScores.length} ${t("games.round")}s | ${t("games.difficulty." + difficulty)}`}
        onPlayAgain={resetGame}
        onBack={onBack}
        t={t}
      >
        <div className="grid grid-cols-5 gap-2 max-w-sm mx-auto mt-2">
          {roundScores.map((rs, i) => (
            <div key={`rs-${i}`} className="text-center p-2 rounded-lg bg-sky-50">
              <p className="text-xs text-slate-400">R{rs.round}</p>
              <p className="text-sm font-medium text-sky-700">+{rs.score}</p>
            </div>
          ))}
        </div>
      </GameOverScreen>
    );
  }

  // Crashed
  if (gameState === "crashed") {
    return (
      <div className="text-center space-y-6 animate-in fade-in duration-300" data-testid="crash-screen">
        <div className="text-6xl animate-bounce">💥</div>
        <p className="text-2xl text-red-500 font-medium">{t("games.reactionTest.crashed")}</p>
        <p className="text-slate-500">{t("games.reactionTest.tooSlow")}</p>
        <p className="text-lg text-sky-800">{t("games.finalScore")}: {score}</p>
        <div className="flex gap-3 justify-center">
          <Button onClick={resetGame} className="glow-button" data-testid="try-again-btn">
            <RotateCcw className="w-4 h-4 mr-2" /> {t("games.tryAgain")}
          </Button>
          <Button variant="outline" onClick={onBack} data-testid="back-btn">{t("games.backToGames")}</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="text-center space-y-4" data-testid="reaction-game-active">
      {/* HUD */}
      <div className="flex justify-between items-center mb-2">
        <div>
          <p className="text-xs text-slate-400">{t("games.round")}</p>
          <p className="text-lg font-medium text-sky-800">{round}/{configRef.current?.rounds}</p>
        </div>
        <div>
          <p className="text-xs text-slate-400">{t("common.score")}</p>
          <p className="text-lg font-medium text-sky-800">{score}</p>
        </div>
        <div>
          <p className="text-xs text-slate-400">{t("games.difficulty." + difficulty)}</p>
          <p className="text-lg font-medium text-amber-500">
            {(configRef.current.baseSpeed + (round - 1) * configRef.current.speedIncrease).toFixed(1)}x
          </p>
        </div>
      </div>

      {/* Countdown */}
      {gameState === "countdown" && (
        <div className="flex items-center justify-center h-48">
          <div className="text-7xl font-light text-sky-400 animate-pulse" style={{ fontFamily: "Outfit" }}>
            {countdown > 0 ? countdown : t("games.reactionTest.go")}
          </div>
        </div>
      )}

      {/* Road scene */}
      {(gameState === "driving" || gameState === "braking" || gameState === "roundEnd") && (
        <>
          <div
            ref={roadRef}
            className="relative w-full h-32 bg-gradient-to-b from-emerald-100 via-emerald-200 to-emerald-300 rounded-2xl overflow-hidden cursor-pointer select-none"
            onClick={handleBrake}
            onTouchStart={handleBrake}
            data-testid="road-area"
          >
            {/* Road surface */}
            <div className="absolute bottom-0 left-0 right-0 h-16 bg-slate-600 rounded-b-2xl">
              <div className="absolute top-1/2 left-0 right-0 h-1 flex gap-4 px-4 -translate-y-1/2">
                {Array.from({ length: 20 }).map((_, i) => (
                  <div key={`dash-${i}`} className="w-8 h-1 bg-yellow-300 rounded flex-shrink-0" />
                ))}
              </div>
            </div>

            {/* Trees bg */}
            <div className="absolute top-2 left-4 text-2xl opacity-40">🌲</div>
            <div className="absolute top-1 left-1/4 text-xl opacity-30">🌳</div>
            <div className="absolute top-3 right-1/4 text-2xl opacity-35">🌲</div>
            <div className="absolute top-1 right-8 text-xl opacity-30">🌳</div>

            {/* Car */}
            <div
              className="absolute bottom-4 text-4xl transition-transform duration-75"
              style={{ left: `${Math.min(progress, 95)}%`, transform: "translateX(-50%) scaleX(-1)" }}
              data-testid="car"
            >
              {CAR_EMOJI}
            </div>

            {/* Rabbit */}
            <div
              className="absolute bottom-4 right-4 text-3xl"
              style={{ animation: gameState === "driving" ? "bounce 0.5s infinite" : "none" }}
              data-testid="rabbit"
            >
              {RABBIT_EMOJI}
            </div>
          </div>

          {/* Action hint */}
          {gameState === "driving" && (
            <div className="animate-pulse">
              <p className="text-red-500 font-medium text-lg">{t("games.reactionTest.tapToBrake")}</p>
              <p className="text-xs text-slate-400">{t("games.reactionTest.orSpace")}</p>
            </div>
          )}

          {gameState === "braking" && (
            <p className="text-amber-500 font-medium animate-pulse">{t("games.reactionTest.braking")}</p>
          )}

          {gameState === "roundEnd" && (
            <div className="space-y-3 animate-in fade-in duration-300" data-testid="round-end">
              <p className="text-emerald-500 font-medium text-lg">{t("games.reactionTest.safe")}</p>
              <p className="text-slate-500 text-sm">
                {t("games.reactionTest.stoppedAt")} {Math.round(rabbitPos - carPos)}px
              </p>
              <p className="text-sky-700 font-medium">
                +{roundScores[roundScores.length - 1]?.score} {t("common.points")}
              </p>
              <Button
                onClick={nextRound}
                className="glow-button"
                data-testid="next-round-btn"
              >
                {round >= configRef.current?.rounds ? t("games.reactionTest.seeResults") : t("common.next")}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ReactionTestGame;
